/**
 * IndexedDB-based upload queue for substance files.
 * Provides persistent storage of pending uploads that survive page reload and browser restart.
 */

import type { QueuedUpload, UploadStatus } from './substance-upload-types.js';

const DB_NAME = 'substance-upload-queue';
const DB_VERSION = 1;
const STORE_NAME = 'uploads';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDatabase(): Promise<IDBDatabase> {
  if (dbPromise) {
    return dbPromise;
  }

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('[UploadQueue] Failed to open database:', request.error);
      dbPromise = null;
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('status', 'status', { unique: false });
        store.createIndex('organizationId', 'organizationId', { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      }
    };
  });

  return dbPromise;
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Add files to the upload queue.
 * Files are sorted by name for stable ordering and all share the same batchUploadDateTime.
 */
export async function queueFiles(organizationId: string, files: File[]): Promise<QueuedUpload[]> {
  const db = await openDatabase();
  const queued: QueuedUpload[] = [];

  // Generate a single batch upload datetime for all files in this batch
  // Use UTC ISO format with Z suffix - this is what server expects
  const batchUploadDateTime = new Date().toISOString();

  // Sort files by name for stable upload order
  const sortedFiles = [...files].sort((a, b) => a.name.localeCompare(b.name, 'nb-NO'));

  for (const file of sortedFiles) {
    const arrayBuffer = await file.arrayBuffer();
    const upload: QueuedUpload = {
      id: generateId(),
      organizationId,
      fileName: file.name,
      fileData: arrayBuffer,
      fileType: file.type,
      status: 'pending',
      createdAt: Date.now(),
      updatedAt: Date.now(),
      batchUploadDateTime,
      retryCount: 0,
    };

    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.add(upload);

      request.onsuccess = () => {
        queued.push(upload);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  console.log(
    `[UploadQueue] Queued ${queued.length} files for organization ${organizationId} with batch time ${batchUploadDateTime}`,
  );
  return queued;
}

/**
 * Get pending uploads for processing (only 'pending' status, NOT failed).
 * Failed uploads require explicit retry via retryFailedUploads().
 */
export async function getPendingUploads(organizationId?: string): Promise<QueuedUpload[]> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => {
      let uploads = request.result as QueuedUpload[];

      // Filter by organization if specified
      if (organizationId) {
        uploads = uploads.filter((u) => u.organizationId === organizationId);
      }

      // Only pending uploads - failed uploads require explicit retry
      uploads = uploads.filter((u) => u.status === 'pending');

      // Sort by createdAt
      uploads.sort((a, b) => a.createdAt - b.createdAt);

      resolve(uploads);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get count of pending uploads across all organizations
 */
export async function getPendingUploadCount(): Promise<number> {
  const uploads = await getPendingUploads();
  return uploads.length;
}

/**
 * Get failed uploads for an organization (for retry functionality)
 */
export async function getFailedUploads(organizationId?: string): Promise<QueuedUpload[]> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => {
      let uploads = request.result as QueuedUpload[];

      // Filter by organization if specified
      if (organizationId) {
        uploads = uploads.filter((u) => u.organizationId === organizationId);
      }

      // Only failed uploads
      uploads = uploads.filter((u) => u.status === 'failed');

      resolve(uploads);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Update the status of an upload
 */
export async function updateUploadStatus(
  id: string,
  status: UploadStatus,
  extra?: {
    error?: string;
    substanceUuid?: string;
    retryCount?: number;
    displayError?: string;
    isDuplicate?: boolean;
    duplicateDetectedAt?: number;
  },
): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const upload = getRequest.result as QueuedUpload | undefined;
      if (!upload) {
        reject(new Error(`Upload ${id} not found`));
        return;
      }

      upload.status = status;
      upload.updatedAt = Date.now();
      if (extra?.error !== undefined) upload.error = extra.error;
      if (extra?.substanceUuid !== undefined) upload.substanceUuid = extra.substanceUuid;
      if (extra?.retryCount !== undefined) upload.retryCount = extra.retryCount;
      if (extra?.displayError !== undefined) upload.displayError = extra.displayError;
      if (extra?.isDuplicate !== undefined) upload.isDuplicate = extra.isDuplicate;
      if (extra?.duplicateDetectedAt !== undefined) upload.duplicateDetectedAt = extra.duplicateDetectedAt;

      const putRequest = store.put(upload);
      putRequest.onsuccess = () => resolve();
      putRequest.onerror = () => reject(putRequest.error);
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
}

/**
 * Get all uploads for display (including duplicates that should be shown temporarily)
 */
export async function getAllUploadsForDisplay(organizationId?: string): Promise<QueuedUpload[]> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => {
      let uploads = request.result as QueuedUpload[];

      // Filter by organization if specified
      if (organizationId) {
        uploads = uploads.filter((u) => u.organizationId === organizationId);
      }

      // Include pending, failed, uploading, AND duplicates that haven't expired yet
      const now = Date.now();
      const DUPLICATE_DISPLAY_DURATION = 15000; // 15 seconds

      uploads = uploads.filter((u) => {
        // Always show pending, uploading, or failed
        if (u.status === 'pending' || u.status === 'uploading' || u.status === 'failed') {
          return true;
        }
        // Show completed duplicates for 15 seconds
        if (u.status === 'completed' && u.isDuplicate && u.duplicateDetectedAt) {
          return now - u.duplicateDetectedAt < DUPLICATE_DISPLAY_DURATION;
        }
        return false;
      });

      // Sort by filename for stable display order
      uploads.sort((a, b) => a.fileName.localeCompare(b.fileName, 'nb-NO'));

      resolve(uploads);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Remove expired duplicate uploads from the queue
 */
export async function removeExpiredDuplicates(): Promise<number> {
  const db = await openDatabase();
  const DUPLICATE_DISPLAY_DURATION = 15000; // 15 seconds
  const now = Date.now();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => {
      const uploads = request.result as QueuedUpload[];
      const expiredDuplicates = uploads.filter(
        (u) =>
          u.status === 'completed' &&
          u.isDuplicate &&
          u.duplicateDetectedAt &&
          now - u.duplicateDetectedAt >= DUPLICATE_DISPLAY_DURATION,
      );

      let removed = 0;
      if (expiredDuplicates.length === 0) {
        resolve(0);
        return;
      }

      let remaining = expiredDuplicates.length;
      for (const upload of expiredDuplicates) {
        const deleteRequest = store.delete(upload.id);
        deleteRequest.onsuccess = () => {
          removed++;
          remaining--;
          if (remaining === 0) resolve(removed);
        };
        deleteRequest.onerror = () => {
          remaining--;
          if (remaining === 0) resolve(removed);
        };
      }
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Remove failed uploads that have been in failed state for more than 1 hour.
 * Only removes uploads with status 'failed' - does not touch pending/uploading
 * items that might need to be resumed after machine restart.
 * Uses updatedAt as the failure timestamp (set when status changes to 'failed').
 */
export async function removeExpiredFailedUploads(): Promise<number> {
  const db = await openDatabase();
  const FAILED_EXPIRY_DURATION = 60 * 60 * 1000; // 1 hour
  const now = Date.now();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => {
      const uploads = request.result as QueuedUpload[];
      const expiredFailed = uploads.filter((u) => u.status === 'failed' && now - u.updatedAt >= FAILED_EXPIRY_DURATION);

      if (expiredFailed.length === 0) {
        resolve(0);
        return;
      }

      console.log(`[UploadQueue] Removing ${expiredFailed.length} expired failed uploads`);

      let removed = 0;
      let remaining = expiredFailed.length;
      for (const upload of expiredFailed) {
        const deleteRequest = store.delete(upload.id);
        deleteRequest.onsuccess = () => {
          removed++;
          remaining--;
          if (remaining === 0) resolve(removed);
        };
        deleteRequest.onerror = () => {
          remaining--;
          if (remaining === 0) resolve(removed);
        };
      }
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Remove a completed upload from the queue
 */
export async function removeUpload(id: string): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Remove all completed uploads
 */
export async function removeCompletedUploads(): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => {
      const uploads = request.result as QueuedUpload[];
      const completed = uploads.filter((u) => u.status === 'completed');

      let remaining = completed.length;
      if (remaining === 0) {
        resolve();
        return;
      }

      for (const upload of completed) {
        const deleteRequest = store.delete(upload.id);
        deleteRequest.onsuccess = () => {
          remaining--;
          if (remaining === 0) resolve();
        };
        deleteRequest.onerror = () => reject(deleteRequest.error);
      }
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Convert a QueuedUpload back to a File object for uploading
 */
export function queuedUploadToFile(upload: QueuedUpload): File {
  const blob = new Blob([upload.fileData], { type: upload.fileType || 'application/pdf' });
  return new File([blob], upload.fileName, { type: upload.fileType || 'application/pdf' });
}

/**
 * Event dispatched when upload queue changes
 */
export class UploadQueueChangedEvent extends Event {
  static readonly TYPE = 'upload-queue-changed';

  constructor(public readonly pendingCount: number) {
    super(UploadQueueChangedEvent.TYPE, { bubbles: true, composed: true });
  }
}

/**
 * Dispatch an event to notify listeners that the queue has changed
 */
export async function notifyQueueChanged(): Promise<void> {
  const count = await getPendingUploadCount();
  window.dispatchEvent(new UploadQueueChangedEvent(count));
}
